import { enterSandbox } from "@commerce-os/contracts";
import { Button, Card, CardContent } from "@commerce-os/ui";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { Loader2Icon } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import { useApp } from "../app/context";
import { useT } from "../app/i18n";
import { meQueryKey } from "../app/me";
import { demoSiteUrl, keepDemoLinks } from "../features/sandbox/links";

/**
 * SB-05 (docs/specs/sandbox.md): the sandbox's way in. The one-time code (and the POS and shop links) arrive in the
 * address fragment, which no server sees; the code becomes the demo Owner's session and the fragment is cleared.
 * Outside the `_auth` layout: a new demo replaces whatever session this tab had.
 */
export const Route = createFileRoute("/sandbox-enter")({ component: SandboxEnterPage });

function SandboxEnterPage() {
  const { t } = useT();
  const { api, session, queryClient } = useApp();
  const navigate = useNavigate();
  const [failed, setFailed] = useState(false);
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    const fragment = new URLSearchParams(window.location.hash.slice(1));
    const code = fragment.get("code");
    keepDemoLinks(fragment.get("pos"), fragment.get("shop"));
    window.history.replaceState(null, "", window.location.pathname + window.location.search);
    if (code === null) {
      setFailed(true);
      return;
    }
    session.clear();
    api
      .call(enterSandbox, { body: { code } })
      .then(async (result) => {
        session.setToken(result.accessToken);
        queryClient.removeQueries({ queryKey: meQueryKey });
        await navigate({ to: "/" });
      })
      .catch(() => {
        setFailed(true);
      });
  }, [api, session, queryClient, navigate]);

  const site = demoSiteUrl();
  return (
    <div className="grid min-h-dvh place-items-center bg-background p-4">
      <Card className="w-full max-w-sm">
        <CardContent className="flex flex-col items-center gap-4 py-10 text-center">
          {failed ? (
            <>
              <p role="alert" className="font-medium">
                {t("sandbox.enter.failed")}
              </p>
              {site ? (
                <Button asChild>
                  <a href={site}>{t("sandbox.enter.again")}</a>
                </Button>
              ) : null}
            </>
          ) : (
            <>
              <Loader2Icon
                aria-hidden
                className="size-6 animate-spin text-primary motion-reduce:animate-none"
              />
              <p className="font-medium">{t("sandbox.enter.title")}</p>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
